import React from 'react'
import { Link, useLocation } from 'react-router-dom'

interface Props {
  label: string
  icon: string
  /** Internal route, or an absolute URL when `external` is set. */
  to: string
  external?: boolean
  expanded: boolean
  /** Rendered but unclickable — a preview of what picking a business unlocks. */
  disabled?: boolean
}

// A bare `i { color }` in public/sass/text.scss paints every icon in the app a
// dark slate. It beats the colour the row inherits down, so the glyph has to
// carry its own class or it renders at ~1.3:1 against the rail.
const ROW = {
  disabled: 'cursor-not-allowed',
  active: 'bg-mm-neutral-1-50',
  idle: '',
}

const ICON = {
  disabled: 'text-mm-neutral-2-400',
  active: 'text-mm-primary-500',
  idle: 'text-mm-neutral-1-50 group-hover:text-mm-primary-400',
}

const LABEL = {
  disabled: 'text-mm-neutral-2-400',
  active: 'text-mm-primary-500',
  idle: 'text-mm-primary-200 group-hover:text-white',
}

export const RailLink = ({ label, icon, to, external, expanded, disabled }: Props) => {
  const location = useLocation()
  const isActive = !external && !disabled && location.pathname.includes(to)
  const state = disabled ? 'disabled' : isActive ? 'active' : 'idle'

  const body = (
    <>
      {/* Solid, not duotone: duotone draws the bulk of a glyph at 40% opacity,
          which on the rail's navy ground leaves the icon almost unreadable. */}
      <i
        className={`fas fa-${icon} w-[40px] flex-shrink-0 text-center text-[20px] leading-[40px] ${ICON[state]}`}
        aria-hidden
      />
      <span
        className={`overflow-hidden whitespace-nowrap text-[14px] font-medium transition-all duration-200 ${
          LABEL[state]
        } ${expanded ? 'ml-1 max-w-full opacity-100' : 'max-w-0 opacity-0'}`}
      >
        {label}
      </span>
    </>
  )

  const className = `group flex h-[40px] items-center rounded-lg transition-colors ${ROW[state]}`

  // Collapsed, the icon is the only label there is, so the native tooltip is
  // what makes an unfamiliar glyph readable.
  const title = expanded ? undefined : label

  if (disabled) {
    return (
      <div className={className} title={`${label} — pick a business first`} aria-disabled>
        {body}
      </div>
    )
  }

  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={className} title={title}>
        {body}
      </a>
    )
  }

  return (
    <Link
      to={`/${to}`}
      className={className}
      title={title}
      aria-current={isActive ? 'page' : undefined}
    >
      {body}
    </Link>
  )
}
